/**
 * Carrinho: gaveta lateral e página do carrinho.
 * - Ícone do carrinho e "Adicionar ao carrinho" abrem a gaveta sem sair da página.
 * - Quantidade, remover, cupom e sugestões atualizam via AJAX e re-renderizam o conteúdo
 *   pela Section Rendering API (o HTML vem sempre do Liquid, sem duplicar marcação aqui).
 * Sem este script tudo continua funcionando por links e formulários comuns (/cart/change, /cart/add).
 */
(() => {
  if (window.themeCart) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const drawer = document.querySelector('[data-cart-drawer]');
  let busy = false;

  const post = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  };

  const roots = () => Array.from(document.querySelectorAll('[data-cart-contents]'));

  const setLoading = (loading) => {
    busy = loading;
    roots().forEach((el) => {
      el.classList.toggle('is-loading', loading);
      el.setAttribute('aria-busy', String(loading));
    });
  };

  const showError = () => {
    roots().forEach((el) => {
      const error = el.querySelector('[data-cart-error]');
      if (error) error.hidden = false;
    });
  };

  // Contador do ícone do carrinho no cabeçalho
  const updateBadge = (count) => {
    document.querySelectorAll('.header__cart').forEach((link) => {
      let badge = link.querySelector('.header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'header__cart-count';
          link.append(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
      const label = link.getAttribute('aria-label');
      if (label) link.setAttribute('aria-label', label.replace(/\d+/, String(count)));
    });
  };

  // Busca o HTML atualizado de cada seção que renderiza o carrinho e troca só o conteúdo
  const refresh = async () => {
    const current = roots();
    const ids = Array.from(new Set(current.map((el) => el.dataset.sectionId)));

    const documents = await Promise.all(
      ids.map(async (id) => {
        const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`, {
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(String(response.status));
        return [id, new DOMParser().parseFromString(await response.text(), 'text/html')];
      })
    );

    let count = 0;
    documents.forEach(([id, doc]) => {
      const fresh = doc.querySelector('[data-cart-contents]');
      if (!fresh) return;
      count = Number(fresh.dataset.itemCount) || 0;

      current
        .filter((el) => el.dataset.sectionId === id)
        .forEach((el) => {
          const scroller = el.querySelector('.cart-contents__main');
          const scrollTop = scroller ? scroller.scrollTop : 0;
          el.replaceWith(fresh.cloneNode(true));
          const next = document.querySelector(`[data-cart-contents][data-section-id="${id}"] .cart-contents__main`);
          if (next) next.scrollTop = scrollTop;
        });

      const label = document.querySelector('[data-cart-count-label]');
      if (label) label.textContent = count > 0 ? fresh.dataset.countLabel : '';
    });

    updateBadge(count);
  };

  const open = () => {
    if (drawer && !drawer.open) drawer.showModal();
  };

  const close = () => {
    if (drawer && drawer.open) drawer.close();
  };

  // Executa uma alteração no carrinho e atualiza a tela; em caso de erro mostra o aviso
  const run = async (action) => {
    if (busy) return;
    setLoading(true);
    try {
      await action();
      await refresh();
    } catch (error) {
      showError();
    } finally {
      setLoading(false);
    }
  };

  const hasCoupon = (cart, code) => {
    const wanted = code.toLowerCase();
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.some((entry) => entry.applicable !== false && String(entry.code).toLowerCase() === wanted);
    }
    const inCart = (cart.cart_level_discount_applications || []).some((entry) => String(entry.title).toLowerCase() === wanted);
    const inItems = (cart.items || []).some((item) => (item.discounts || []).some((entry) => String(entry.title).toLowerCase() === wanted));
    return inCart || inItems;
  };

  const applyCoupon = async (form) => {
    const input = form.querySelector('input[name="discount"]');
    const code = input ? input.value.trim() : '';
    if (!code || busy) return;

    const errorMessage = form.parentElement.querySelector('[data-cart-coupon-error]');
    if (errorMessage) errorMessage.hidden = true;
    setLoading(true);

    try {
      const cart = await post('cart/update.js', { discount: code });
      if (hasCoupon(cart, code)) {
        await refresh();
      } else {
        // Remove o código inválido para não ficar guardado no carrinho
        await post('cart/update.js', { discount: '' });
        if (errorMessage) errorMessage.hidden = false;
      }
    } catch (error) {
      showError();
    } finally {
      setLoading(false);
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Ícone do carrinho no cabeçalho
    const opener = target.closest('[data-cart-open]');
    if (opener && drawer && !(event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) {
      event.preventDefault();
      open();
      return;
    }

    if (target.closest('[data-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    // Clique no fundo escurecido, fora do painel
    if (drawer && target === drawer) {
      const rect = drawer.getBoundingClientRect();
      const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      if (outside) close();
      return;
    }

    // Quantidade e remover
    const change = target.closest('[data-cart-change]');
    if (change) {
      event.preventDefault();
      const { key, quantity } = change.dataset;
      run(() => post('cart/change.js', { id: key, quantity: Number(quantity) }));
      return;
    }

    if (target.closest('[data-cart-coupon-remove]')) {
      event.preventDefault();
      run(() => post('cart/update.js', { discount: '' }));
      return;
    }

    // Sacola Lumina: "Adicionar" de "Complete sua rotina" coloca 1 unidade e atualiza a sacola
    const quickAdd = target.closest('[data-cart-add]');
    if (quickAdd) {
      event.preventDefault();
      run(() => post('cart/add.js', { items: [{ id: Number(quickAdd.dataset.cartAdd), quantity: 1 }] }));
      return;
    }

    // Sacola Lumina vazia: o botão leva a outra parte do site, então fecha a gaveta antes
    if (target.closest('[data-cart-close-link]')) {
      close();
      return;
    }

    // Sugestões do carrinho vazio
    const suggestion = target.closest('[data-cart-suggestion]');
    if (suggestion) {
      const pressed = suggestion.getAttribute('aria-pressed') === 'true';
      suggestion.setAttribute('aria-pressed', String(!pressed));
      const container = suggestion.closest('[data-cart-contents]');
      const addButton = container && container.querySelector('[data-cart-suggestions-add]');
      if (addButton) addButton.disabled = !container.querySelector('[data-cart-suggestion][aria-pressed="true"]');
      return;
    }

    const addSelected = target.closest('[data-cart-suggestions-add]');
    if (addSelected) {
      const container = addSelected.closest('[data-cart-contents]');
      const items = Array.from(container.querySelectorAll('[data-cart-suggestion][aria-pressed="true"]')).map((card) => ({
        id: Number(card.dataset.variantId),
        quantity: 1,
      }));
      if (items.length) run(() => post('cart/add.js', { items }));
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const coupon = form.closest('[data-cart-coupon]') || (form.matches('[data-cart-coupon]') ? form : null);
    if (coupon) {
      event.preventDefault();
      applyCoupon(form);
      return;
    }

    // "Adicionar ao carrinho" de qualquer formulário de produto: abre a gaveta em vez de ir para /cart
    if (!drawer || event.defaultPrevented) return;
    if (!/\/cart\/add\/?$/.test(new URL(form.action, window.location.href).pathname)) return;

    event.preventDefault();
    const button = event.submitter;
    if (button) button.disabled = true;

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error(String(response.status));
      await refresh();
      open();
    } catch (error) {
      // Se algo falhar, envia o formulário do jeito tradicional
      form.submit();
    } finally {
      if (button) button.disabled = false;
    }
  });

  // Voltar/avançar do navegador pode trazer o carrinho antigo do cache
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh().catch(() => {});
  });

  window.themeCart = { open, close, refresh };
})();
